import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge, OccurrenceBadges } from "@/components/StatusUI";
import { fmtDate } from "@/lib/meta";
import { Card } from "@/components/ui/card";
import { QrCode, PackageCheck, AlertTriangle, ChevronRight, Loader2, Truck } from "lucide-react";

export default function HomeOperacional() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [aguard, setAguard] = useState(null);
  const [ocs, setOcs] = useState([]);
  const [ultimos, setUltimos] = useState([]);

  const load = useCallback(async () => {
    const [t, o, all] = await Promise.all([
      api.get("/pedidos?status=EM_TRANSITO"),
      api.get("/ocorrencias?status=ABERTA"),
      api.get("/pedidos"),
    ]);
    setAguard(t.data);
    setOcs(o.data);
    setUltimos(all.data.filter((p) => ["RECEBIDO", "CONCLUIDO"].includes(p.status)).slice(0, 6));
  }, []);

  useEffect(() => {
    load();
    const iv = setInterval(load, 9000);
    const onVis = () => { if (document.visibilityState === "visible") load(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(iv); document.removeEventListener("visibilitychange", onVis); };
  }, [load]);

  const unidadeNome = user.unidades?.length === 1 ? user.unidades[0].nome : null;

  return (
    <div className="space-y-5" data-testid="home-operacional">
      <div>
        <div className="text-[11px] sm:text-xs text-primary uppercase tracking-[0.18em] font-bold">
          {unidadeNome || "Recebimento"}
        </div>
        <h1 className="font-display text-2xl sm:text-4xl font-extrabold uppercase">Olá, {user.name.split(" ")[0]}</h1>
      </div>

      {/* Botão principal — LER QR / RECEBER */}
      <button
        onClick={() => nav("/qr")}
        data-testid="home-ler-qr"
        className="w-full rounded-2xl bg-primary text-primary-foreground p-6 sm:p-8 flex items-center gap-4 shadow-[0_10px_40px_-10px_rgba(245,158,11,0.6)] active:scale-[0.99] transition-transform"
      >
        <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl bg-black/15 flex items-center justify-center shrink-0">
          <QrCode className="w-9 h-9 sm:w-11 sm:h-11" strokeWidth={2.2} />
        </div>
        <div className="text-left leading-tight">
          <div className="font-display text-2xl sm:text-3xl font-extrabold uppercase">Ler QR / Receber</div>
          <div className="text-sm opacity-90">Aponte a câmera para a etiqueta do volume</div>
        </div>
        <ChevronRight className="w-7 h-7 ml-auto shrink-0 opacity-80" />
      </button>

      {/* Recebimentos aguardando */}
      <Card className="bg-card/70 p-4" data-testid="home-aguardando">
        <div className="flex items-center gap-2 mb-3">
          <Truck className="w-5 h-5 text-primary" />
          <div className="font-display text-base sm:text-lg font-bold uppercase">Recebimentos Aguardando</div>
          {aguard && <span className="ml-auto font-mono font-bold text-primary text-lg">{aguard.length}</span>}
        </div>
        {!aguard ? (
          <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>
        ) : aguard.length === 0 ? (
          <div className="text-sm text-muted-foreground py-4 text-center">Nenhum pedido em trânsito para receber.</div>
        ) : (
          <div className="space-y-2">
            {aguard.map((p) => (
              <button key={p.id} onClick={() => nav("/recebimento")} data-testid={`home-receber-${p.numero}`}
                className="w-full flex items-center justify-between gap-2 rounded-md border border-border bg-secondary/40 active:bg-secondary transition-colors px-3 py-2.5 text-left">
                <div className="min-w-0">
                  <div className="font-mono font-bold text-sm truncate flex items-center gap-1.5"><PackageCheck className="w-4 h-4 text-primary shrink-0" /> {p.numero}</div>
                  <div className="text-xs text-muted-foreground">{p.itens.length} itens · {fmtDate(p.created_at)}</div>
                </div>
                <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
              </button>
            ))}
          </div>
        )}
      </Card>

      {/* Ocorrências da unidade */}
      <Card className="bg-card/70 p-4" data-testid="home-ocorrencias">
        <div className="flex items-center gap-2 mb-3">
          <AlertTriangle className="w-5 h-5 text-rose-300" />
          <div className="font-display text-base sm:text-lg font-bold uppercase">Ocorrências da Unidade</div>
          <span className="ml-auto font-mono font-bold text-rose-300 text-lg">{ocs.length}</span>
        </div>
        {ocs.length === 0 ? (
          <div className="text-sm text-muted-foreground py-4 text-center">Nenhuma ocorrência aberta.</div>
        ) : (
          <div className="space-y-2">
            {ocs.slice(0, 6).map((o) => (
              <div key={o.id} className="flex items-center justify-between gap-2 border-b border-border pb-2 last:border-0" data-testid={`home-oc-${o.id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{o.sku_nome || o.pedido_numero || "Ocorrência"}</div>
                  <div className="text-[11px] text-muted-foreground font-mono">{o.pedido_numero}</div>
                </div>
                <OccurrenceBadges tipos={[o.tipo]} />
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Últimos recebimentos */}
      <Card className="bg-card/70 p-4" data-testid="home-ultimos">
        <div className="font-display text-base sm:text-lg font-bold uppercase mb-3">Últimos Recebimentos</div>
        {ultimos.length === 0 ? (
          <div className="text-sm text-muted-foreground py-4 text-center">Ainda não há recebimentos concluídos.</div>
        ) : (
          <div className="divide-y divide-border">
            {ultimos.map((p) => (
              <div key={p.id} onClick={() => nav(`/pedidos/${p.id}`)} data-testid={`home-ultimo-${p.numero}`}
                className="flex items-center justify-between gap-2 py-2.5 cursor-pointer hover:bg-secondary/40 px-2 -mx-2 rounded">
                <div className="min-w-0">
                  <div className="font-mono font-bold text-sm truncate">{p.numero}</div>
                  <div className="text-xs text-muted-foreground">{fmtDate(p.created_at)}</div>
                </div>
                <StatusBadge status={p.status} />
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
